import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, API } from '../App';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [status, setStatus] = useState('polling');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 8;

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    pollPaymentStatus();
  }, []);

  const pollPaymentStatus = async (attempt = 0) => {
    if (attempt >= maxAttempts) {
      setStatus('timeout');
      return;
    }
    try {
      const response = await axios.get(`${API}/stripe/checkout-status/${sessionId}`);
      const data = response.data;
      if (data.paymentStatus === 'paid') {
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }
      setAttempts(attempt + 1);
      setTimeout(() => pollPaymentStatus(attempt + 1), 2500);
    } catch (error) {
      console.error('Error polling payment status:', error);
      if (attempt < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(attempt + 1), 2500);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-600 to-purple-800 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 text-center" data-testid="payment-result-card">
        {status === 'polling' && (
          <>
            <div className="flex justify-center mb-6">
              <Loader2 className="h-16 w-16 text-purple-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="payment-polling-title">
              {t('paymentVerifying')}
            </h1>
            <p className="text-gray-600 mb-4">{t('paymentVerifyingDesc')}</p>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
              <div
                className="bg-purple-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${(attempts / maxAttempts) * 100}%` }}
              />
            </div>
            <p className="text-xs text-gray-400">{t('paymentChecking', { current: attempts, total: maxAttempts })}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="payment-success-title">
              {t('paymentSuccessTitle')}
            </h1>
            <p className="text-gray-600 mb-2">{t('paymentSuccessDesc')}</p>
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6 text-left">
              <p className="text-sm text-purple-800 font-medium mb-1">{t('paymentNextSteps')}</p>
              <ul className="text-sm text-purple-700 space-y-1">
                <li>- {t('paymentNextStep1')}</li>
                <li>- {t('paymentNextStep2')}</li>
                <li>- {t('paymentNextStep3')}</li>
              </ul>
            </div>
            <button
              onClick={() => navigate('/business/dashboard')}
              className="w-full py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
              data-testid="payment-success-continue-btn"
            >
              {t('goToDashboard')}
            </button>
            <p className="text-xs text-gray-500 mt-4" data-testid="contact-support-before-chargeback-note">
              {t('contactSupportBeforeChargeback', 'Need help with your subscription later? Email')}{' '}
              <a href="mailto:support@find-2-day.com" className="text-purple-600 underline">support@find-2-day.com</a>
              {' '}— {t('contactSupportBeforeChargebackTail', 'we resolve refund and billing issues directly, faster than a chargeback.')}
            </p>
          </>
        )}

        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <>
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="h-12 w-12 text-red-600" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="payment-error-title">
              {status === 'expired' ? t('paymentExpiredTitle') : t('paymentErrorTitle')}
            </h1>
            <p className="text-gray-600 mb-6">
              {status === 'expired' ? t('paymentExpiredDesc') : t('paymentErrorDesc')}
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/signin')}
                className="w-full py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
                data-testid="payment-error-signin-btn"
              >
                {t('signIn')}
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
                data-testid="payment-error-home-btn"
              >
                {t('goHome')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
